import { useEffect, useId, useRef, useState } from 'react';
import api from '../../utils/api';
import { gateAllows } from '../../utils/hubs/registry';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { startIsolateBatch } from '../../utils/spores/sporeBatch';
import { handOff } from '../../utils/workbench/handoff';
import { sniffFile } from '../workbench/detect';
import { availableModes, fileModes, handedFileMode, modeOf } from '../workbench/modeList';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import Dialog from '../ui/Dialog';
import { Code, dayText } from './cells';
import { here, obviousPlate } from './LabAnalyticsTray';
import { checkablePlate } from './quickActions';

/*
 * "Open in the workbench": where an isolate's picture comes from before the
 * analysis workbench opens on it (2026-10-27). Home's card swipe opens it
 * (IsolatesTable, hooks/useCardReveal.js), and so does the first button of
 * the card's Lab analytics tray (LabAnalyticsTray), the way in without a
 * swipe and the only one on a wide screen.
 *
 *   From this isolate  one tab per kind of picture whose workbench mode the
 *                      reader may open (components/workbench/modeList.js,
 *                      its `gate`), each with how many there are, and the
 *                      pictures as thumbnails, newest first:
 *                        Dishes       lab:read, culture mode: the dossier's
 *                                     photos of the isolate's plates, all
 *                                     but micrographs (the culture mode lists
 *                                     the same: routes/culture_analyses.py)
 *                        Thermal      lab:read, thermal mode: GET
 *                                     /thermal/captures?isolate=
 *                        Micrographs  game:play, microscopy mode: the
 *                                     dossier's `microscopy`, handed over as
 *                                     the mode's batch first, as the tray's
 *                                     Spore button does
 *                      A picture pressed opens its mode on it.
 *   Upload image       for whoever may hand a file to a mode (its
 *                      `writeGate`): the file is sniffed here with the
 *                      workbench's own detection (modeList.js
 *                      handedFileMode: a plain picture is a dish photograph)
 *                      and handed, in memory (utils/workbench/handoff.js), to
 *                      the mode it belongs to, which takes it as if Open a
 *                      file had; one that no mode of theirs takes is said so,
 *                      and the dialog stays
 *   Take a photo       the culture mode's dish camera (lab:observe), on a
 *                      live plate: camera=1 opens it on arrival
 *   Plate              with two live plates or more, the plate an upload
 *                      or a photo goes to (the row's obvious one first)
 *   Open with latest   the newest picture of any kind shown, opened as if
 *                      pressed; with none, the first mode with the
 *                      isolate's material (its plates), else the first the
 *                      reader may open, on the isolate
 *
 * Zero scroll (docs/ANALYSIS_WORKBENCH.md section 2, the same rule as the
 * workbench it leads to): the dialog fits the screen, a phone's included, and
 * only the gallery scrolls; the tabs, the new-picture buttons and the action
 * bar stay in sight (index.css .isp-*).
 *
 * Every way into the workbench is onNavigate(path, {returnTo, handoff?}),
 * the tray's contract: the workbench's Back comes back to this Home address.
 * The dossier is the row's own (useDossiers), never asked for twice. Offered
 * to whoever may open a workbench mode (LabAnalyticsTray's pickerOffered).
 */
const KINDS = Object.freeze([
  { key: 'dishes', mode: 'culture' },
  { key: 'thermal', mode: 'thermal' },
  { key: 'micrographs', mode: 'microscopy' },
]);

/** What Upload image takes (the workbench's own Open a file). */
export const PICKER_ACCEPT = 'image/*,.jpg,.jpeg,.tif,.tiff,.vsi';

const plateCount = (item) => item?.counts?.plates ?? (item?.plates || []).length;
const livePlates = (item) => (item?.plates || []).filter(checkablePlate);
const iconOf = (kind) => modeOf(KINDS.find((k) => k.key === kind)?.mode)?.icon || '';

/**
 * A moment to sort by (ms): an ISO moment, a bare day, or a camera's own EXIF
 * "2026:02:15 10:22:33" (a micrograph's captured_at); 0 when there is none.
 */
export function momentOf(value) {
  if (!value) return 0;
  const s = String(value);
  const exif = /^(\d{4}):(\d{2}):(\d{2})(?:[ T](\d{2}:\d{2}(?::\d{2})?))?/.exec(s);
  const ms = Date.parse(exif ? `${exif[1]}-${exif[2]}-${exif[3]}T${exif[4] || '00:00'}` : s);
  return Number.isFinite(ms) ? ms : 0;
}
const newestFirst = (a, b) => momentOf(b.at) - momentOf(a.at);

/** The isolate's dish photographs: its plates' photos that are not micrographs. */
export function dishesOf(dossier) {
  return (dossier?.photos || [])
    .filter((p) => p.source === 'plate' && p.photo_type !== 'microscopy' && p.photo_id && p.record_id)
    .map((p) => ({ kind: 'dishes', id: p.photo_id, plateId: p.record_id, code: p.record_code || '',
      thumb: p.thumbnail_url || p.s3_url || '', at: p.captured_at || p.uploaded_at || null }))
    .sort(newestFirst);
}

/** Its thermal captures (GET /thermal/captures?isolate=); a plate's code where one is linked. */
export function capturesOf(list) {
  return (list || []).filter((c) => c?.capture_id)
    .map((c) => ({ kind: 'thermal', id: c.capture_id, code: (c.linked || []).find((l) => l.kind === 'plate')?.code || '',
      thumb: c.urls?.thumb || '', at: c.captured_at || c.created_at || null }))
    .sort(newestFirst);
}

/** Its micrographs (the dossier's `microscopy`). */
export function micrographsOf(dossier) {
  return (dossier?.microscopy || []).filter((m) => m?.image_id)
    .map((m) => ({ kind: 'micrographs', id: m.image_id, code: '', thumb: m.thumbnail_url || m.url || '',
      at: m.captured_at || m.uploaded_at || null }))
    .sort(newestFirst);
}

/** A workbench address: the pairs given, in order, the empty ones left out. */
const workbench = (pairs) => `/tools/analysis?${new URLSearchParams(pairs.filter(([, v]) => v))}`;

/** Where a picture opens: its mode, on the isolate and on it. */
export function picturePath(entry, specimenId) {
  if (entry.kind === 'dishes') {
    return workbench([['mode', 'culture'], ['isolate', specimenId], ['plate', entry.plateId], ['photo', entry.id],
      ['src', 'isolate']]);
  }
  if (entry.kind === 'thermal') return workbench([['mode', 'thermal'], ['isolate', specimenId], ['capture', entry.id]]);
  return workbench([['mode', 'microscopy'], ['isolate', specimenId], ['image', entry.id]]);
}

/** A mode on the isolate, with nothing chosen yet; the culture mode on `plate`, its picture from `src`. */
export function modePath(mode, specimenId, { plate = '', src = '', camera = false } = {}) {
  if (mode !== 'culture') return workbench([['mode', mode], ['isolate', specimenId]]);
  return workbench([['mode', 'culture'], ['isolate', specimenId], ['plate', plate], ['src', src],
    ['camera', camera ? '1' : '']]);
}

export default function ImageSourcePicker({ item, user, t, dossiers, onNavigate, onClose }) {
  const id = item.specimen_id;
  const introId = useId();
  const tabsId = useId();
  const kinds = KINDS.filter((k) => gateAllows(user, modeOf(k.mode).gate));
  const culture = modeOf('culture');
  const plates = livePlates(item);
  const mayUpload = fileModes(user).length > 0;
  const mayCamera = gateAllows(user, culture.gate) && gateAllows(user, culture.writeGate) && plates.length > 0;
  const wantsThermal = kinds.some((k) => k.key === 'thermal');
  const needsDossier = kinds.some((k) => k.key !== 'thermal');
  const [plate, setPlate] = useState(() => obviousPlate(item));
  const [chosen, setChosen] = useState(null);
  const [thermal, setThermal] = useState({ state: wantsThermal ? 'loading' : 'none', items: [] });
  const [sniffing, setSniffing] = useState(false);
  const [problem, setProblem] = useState('');
  const fileRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // The row's dossier, read once for the whole hub (useDossiers): asked for if no one has yet.
  useEffect(() => {
    if (needsDossier) dossiers?.ensure?.(id);
  }, [id, needsDossier]); // eslint-disable-line react-hooks/exhaustive-deps
  const entry = dossiers?.get?.(id);
  const dossier = entry?.data || null;

  useEffect(() => {
    if (!wantsThermal) return undefined;
    let live = true;
    api.get('/thermal/captures', { params: { isolate: id } })
      .then((res) => { if (live) setThermal({ state: 'ready', items: Array.isArray(res.data) ? res.data : [] }); })
      .catch(() => { if (live) setThermal({ state: 'error', items: [] }); });
    return () => { live = false; };
  }, [id, wantsThermal]);

  const stateOf = (kind) => {
    if (kind === 'thermal') return thermal.state;
    if (dossier) return 'ready';
    return entry?.error && !entry.loading ? 'error' : 'loading';
  };
  const itemsOf = (kind) => {
    if (kind === 'dishes') return dishesOf(dossier);
    if (kind === 'thermal') return capturesOf(thermal.items);
    return micrographsOf(dossier);
  };
  // The tab shown: the reader's choice, else the first still loading or with pictures, else the first.
  const shown = chosen || kinds.find((k) => stateOf(k.key) === 'loading' || itemsOf(k.key).length > 0)?.key
    || kinds[0]?.key || null;
  const loadingAny = kinds.some((k) => stateOf(k.key) === 'loading');

  const go = (path, extra = {}) => onNavigate(path, { returnTo: here(), ...extra });

  /** A picture pressed: its mode on it (micrographs handed over as the mode's batch first). */
  const pick = (picture) => {
    if (picture.kind === 'micrographs') startIsolateBatch(item, dossier);
    go(picturePath(picture, id));
  };

  /** A file from the device: to the mode it belongs to, with it in hand; else said, and the dialog stays. */
  const upload = async (file) => {
    setProblem('');
    setSniffing(true);
    const target = handedFileMode(await sniffFile(file), user);
    if (!alive.current) return;
    setSniffing(false);
    if (!target) {
      setProblem(t('picker.upload.noMode', { name: file.name || '' }));
      return;
    }
    // An upload in the microscopy mode is filed with the isolate when its micrographs are its batch.
    if (target === 'microscopy') startIsolateBatch(item, dossier);
    const token = handOff(file, { mode: target, isolate: id });
    go(modePath(target, id, target === 'culture' ? { plate, src: 'upload' } : {}), { handoff: token });
  };

  const camera = () => go(modePath('culture', id, { plate, src: 'camera', camera: true }));

  /** The newest picture shown, as if pressed; with none, a mode on the isolate. */
  const latest = () => {
    const newest = kinds.flatMap((k) => itemsOf(k.key)).sort(newestFirst)[0];
    if (newest) {
      pick(newest);
      return;
    }
    const material = { dishes: plateCount(item) > 0, thermal: false, micrographs: false };
    const kind = kinds.find((k) => material[k.key]) || kinds[0];
    const mode = kind?.mode || availableModes(user)[0]?.key;
    if (!mode) return;
    if (mode === 'microscopy') startIsolateBatch(item, dossier);
    go(modePath(mode, id, mode === 'culture' ? { plate, src: 'isolate' } : {}));
  };

  const title = <>{t('picker.title')} <Code>{item.specimen_code}</Code></>;
  const tabId = (key) => `${tabsId}-tab-${key}`;
  const panelId = `${tabsId}-panel`;
  const shownItems = shown ? itemsOf(shown) : [];
  const shownState = shown ? stateOf(shown) : 'none';

  return (
    <Dialog title={title} onClose={onClose} testId="image-picker" width={560} describedBy={introId}
      panelClassName="isp-panel" panelStyle={{ maxHeight: undefined, overflowY: undefined, padding: 16 }}>
      <p id={introId} className="isp-intro">{t('picker.intro')}</p>

      {kinds.length > 0 && (
        <section className="isp-section" aria-labelledby={`${tabsId}-heading`}>
          <h3 id={`${tabsId}-heading`} className="isp-heading">{t('picker.fromIsolate')}</h3>
          <div role="tablist" aria-labelledby={`${tabsId}-heading`} className="isp-kinds" data-testid="image-picker-kinds">
            {kinds.map((k) => {
              const count = itemsOf(k.key).length;
              const state = stateOf(k.key);
              return (
                <button key={k.key} type="button" role="tab" id={tabId(k.key)} aria-selected={shown === k.key}
                  aria-controls={panelId} className="isp-kind" onClick={() => setChosen(k.key)}
                  data-testid={`image-picker-kind-${k.key}`} data-count={state === 'ready' ? count : undefined}>
                  <span aria-hidden="true">{iconOf(k.key)}</span>
                  <span>{t(`picker.kinds.${k.key}`)}</span>
                  <span className="isp-count">{state === 'ready' ? count : '…'}</span>
                </button>
              );
            })}
          </div>
          <div role="tabpanel" id={panelId} aria-labelledby={shown ? tabId(shown) : undefined} className="isp-gallery"
            data-testid="image-picker-gallery" data-kind={shown || undefined}>
            {shownState === 'loading' && <p className="isp-note">{t('picker.loading')}</p>}
            {shownState === 'error' && <p role="alert" className="isp-note">{t('picker.failed')}</p>}
            {shownState === 'ready' && shownItems.length === 0 && (
              <p className="isp-note" data-testid="image-picker-empty">{t(`picker.empty.${shown}`)}</p>
            )}
            {shownItems.length > 0 && (
              <ul className="isp-grid">
                {shownItems.map((picture) => {
                  const when = picture.at ? dayText(picture.at) : t('picker.undated');
                  const name = picture.code
                    ? t(`picker.item.${picture.kind}WithCode`, { when, code: picture.code })
                    : t(`picker.item.${picture.kind}`, { when });
                  const src = resolvePhotoUrl(picture.thumb);
                  return (
                    <li key={picture.id}>
                      <button type="button" className="isp-thumb" aria-label={name} title={name}
                        onClick={() => pick(picture)} data-testid={`image-picker-${picture.kind}-${picture.id}`}>
                        {src ? <img src={src} alt="" loading="lazy" />
                          : <span aria-hidden="true" className="isp-thumb__none">{iconOf(picture.kind)}</span>}
                        <bdi dir="ltr" className="isp-thumb__when">{when}</bdi>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      )}

      {(mayUpload || mayCamera) && (
        <div className="isp-new" data-testid="image-picker-new">
          {plates.length >= 2 && (
            <label className="isp-plate">
              <span>{t('picker.plate.label')}</span>
              <select value={plate} onChange={(e) => setPlate(e.target.value)} title={t('picker.plate.tip')}
                data-testid="image-picker-plate">
                {plates.map((p) => <option key={p.plate_id} value={p.plate_id}>{p.plate_code}</option>)}
              </select>
            </label>
          )}
          <div className="isp-new__buttons">
            {mayUpload && (
              <>
                <Button className="isp-btn" busy={sniffing} onClick={() => fileRef.current?.click()}
                  title={t('picker.upload.tip')} data-testid="image-picker-upload">
                  <span aria-hidden="true">⬆️ </span>{t('picker.upload.label')}
                </Button>
                <input ref={fileRef} type="file" hidden accept={PICKER_ACCEPT} data-testid="image-picker-upload-input"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
              </>
            )}
            {mayCamera && (
              <Button className="isp-btn" onClick={camera} title={t('picker.camera.tip')}
                data-testid="image-picker-camera">
                <span aria-hidden="true">📷 </span>{t('picker.camera.label')}
              </Button>
            )}
          </div>
        </div>
      )}

      {problem && <p role="alert" className="isp-problem" data-testid="image-picker-problem">{problem}</p>}

      <ActionBar style={{ marginBlockStart: 12 }}>
        <Button onClick={onClose} data-testid="image-picker-cancel">{t('picker.cancel')}</Button>
        <Button variant="primary" onClick={latest} busy={loadingAny} title={t('picker.latest.tip')}
          data-testid="image-picker-latest">
          {t('picker.latest.label')}
        </Button>
      </ActionBar>
    </Dialog>
  );
}
